import React, { useState, useEffect } from 'react';
import { getExpenses, createExpense, approveExpense, deleteExpense, getExpenseCategories } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Receipt, Save, MoreHorizontal, CheckCircle, Trash2, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const statusColors = {
  'Pending': 'bg-yellow-500',
  'Approved': 'bg-green-500',
  'Rejected': 'bg-red-500'
};

const paymentMethods = ['Cash', 'Card', 'Bank Transfer', 'Cheque', 'Petty Cash'];

export default function ExpensesPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [newExpense, setNewExpense] = useState({
    category_id: '',
    description: '',
    amount: 0,
    expense_date: new Date().toISOString().split('T')[0],
    vendor_name: '',
    vendor_trn: '',
    reference_number: '',
    payment_method: 'Cash',
    billable: false,
    vat_applicable: true,
    vat_rate: 5,
    notes: ''
  });

  // Calculate VAT amount
  const calculatedVat = newExpense.vat_applicable ? (newExpense.amount * (newExpense.vat_rate / 100)) : 0;
  const totalWithVat = newExpense.amount + calculatedVat;

  useEffect(() => {
    loadExpenses();
    loadCategories();
  }, [filterCategory, filterStatus]);

  const loadExpenses = async () => {
    try {
      const params = { limit: 100 };
      if (filterCategory) params.category_id = filterCategory;
      if (filterStatus) params.status = filterStatus;
      
      const res = await getExpenses(params);
      setExpenses(res.data.items);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await getExpenseCategories();
      setCategories(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreate = async () => {
    if (!newExpense.category_id || !newExpense.description || newExpense.amount <= 0) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      await createExpense(newExpense);
      toast.success('Expense recorded');
      setDialogOpen(false);
      setNewExpense({
        category_id: '',
        description: '',
        amount: 0,
        expense_date: new Date().toISOString().split('T')[0],
        vendor_name: '',
        vendor_trn: '',
        reference_number: '',
        payment_method: 'Cash',
        billable: false,
        vat_applicable: true,
        vat_rate: 5,
        notes: ''
      });
      loadExpenses();
    } catch (error) {
      toast.error('Failed to create expense');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveExpense(id);
      toast.success('Expense approved');
      loadExpenses();
    } catch (error) {
      toast.error('Failed to approve expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await deleteExpense(id);
      toast.success('Expense deleted');
      loadExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const totalAmount = expenses.reduce((sum, e) => sum + (e.total_amount || e.amount), 0);
  const totalVatAmount = expenses.reduce((sum, e) => sum + (e.vat_amount || 0), 0);
  const canApprove = user?.role === 'Admin' || user?.role === 'Manager';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Expenses</h1>
          <p className="text-muted-foreground">Track business expenses with VAT</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-expense-btn">
              <Plus className="w-4 h-4 mr-2" />
              Record Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Record Expense</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
              <div>
                <Label>Category *</Label>
                <Select value={newExpense.category_id} onValueChange={(v) => setNewExpense(p => ({ ...p, category_id: v }))}>
                  <SelectTrigger data-testid="expense-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Description *</Label>
                <Input
                  value={newExpense.description}
                  onChange={(e) => setNewExpense(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description"
                  data-testid="expense-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount (AED) *</Label>
                  <Input
                    type="number"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                    data-testid="expense-amount"
                  />
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={newExpense.expense_date}
                    onChange={(e) => setNewExpense(p => ({ ...p, expense_date: e.target.value }))}
                  />
                </div>
              </div>

              {/* VAT Section */}
              <div className="p-4 rounded-lg bg-slate-50 border space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-muted-foreground" />
                    <Label className="font-medium">VAT Settings</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">VAT Applicable</Label>
                    <Switch
                      checked={newExpense.vat_applicable}
                      onCheckedChange={(v) => setNewExpense(p => ({ ...p, vat_applicable: v }))}
                      data-testid="vat-applicable-switch"
                    />
                  </div>
                </div>
                
                {newExpense.vat_applicable && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>VAT Rate (%)</Label>
                        <Select 
                          value={newExpense.vat_rate.toString()} 
                          onValueChange={(v) => setNewExpense(p => ({ ...p, vat_rate: parseFloat(v) }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5">5% (Standard)</SelectItem>
                            <SelectItem value="0">0% (Zero Rated)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Vendor TRN</Label>
                        <Input
                          value={newExpense.vendor_trn}
                          onChange={(e) => setNewExpense(p => ({ ...p, vendor_trn: e.target.value }))}
                          placeholder="Tax Registration Number"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="text-muted-foreground">VAT Amount:</span>
                      <span className="font-mono">{formatCurrency(calculatedVat)}</span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>Total (incl. VAT):</span>
                      <span className="font-mono text-lg">{formatCurrency(totalWithVat)}</span>
                    </div>
                  </>
                )}
              </div>

              <div>
                <Label>Vendor Name</Label>
                <Input
                  value={newExpense.vendor_name}
                  onChange={(e) => setNewExpense(p => ({ ...p, vendor_name: e.target.value }))}
                  placeholder="Supplier/Vendor"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Method</Label>
                  <Select value={newExpense.payment_method} onValueChange={(v) => setNewExpense(p => ({ ...p, payment_method: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference #</Label>
                  <Input
                    value={newExpense.reference_number}
                    onChange={(e) => setNewExpense(p => ({ ...p, reference_number: e.target.value }))}
                    placeholder="Receipt/Invoice #"
                  />
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={newExpense.notes}
                  onChange={(e) => setNewExpense(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                />
              </div>

              <Button className="w-full" onClick={handleCreate} disabled={saving} data-testid="save-expense-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Record Expense'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex gap-8">
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-semibold font-mono text-red-500">{formatCurrency(totalAmount)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Input VAT</p>
                <p className="text-2xl font-semibold font-mono text-amber-600">{formatCurrency(totalVatAmount)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Count</p>
                <p className="text-2xl font-semibold">{expenses.length}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={filterCategory} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No expenses recorded yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Expense #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">VAT</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((exp) => (
                  <TableRow key={exp.id} data-testid={`expense-row-${exp.id}`}>
                    <TableCell className="font-mono">{exp.expense_number}</TableCell>
                    <TableCell>{formatDate(exp.expense_date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{exp.category_name}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{exp.description}</TableCell>
                    <TableCell className="text-muted-foreground">{exp.vendor_name || '-'}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(exp.amount)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-amber-600">
                      {exp.vat_amount ? formatCurrency(exp.vat_amount) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-red-500">
                      {formatCurrency(exp.total_amount || exp.amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[exp.status] || 'bg-gray-500'}>
                        {exp.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canApprove && exp.status === 'Pending' && (
                            <DropdownMenuItem onClick={() => handleApprove(exp.id)}>
                              <CheckCircle className="w-4 h-4 mr-2" /> Approve
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDelete(exp.id)} className="text-red-500">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {expenses.length} of {total} expenses
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
